/**
 * useShellMapFiles - maps travel as FILES, the way characters always have.
 *
 * The library lives under the session and dies with it, and no backend would
 * make last year's map appear in this morning's session without one. Export
 * writes one map or the whole library; import reads ONE file and reports
 * through a toast; a folder already read (the folder view reports its own
 * count and failures) is only merged. Extracted from DeathWatchSimulator.
 */
import { useCallback } from 'react';
import { toMapFile, readMapFile } from '../engine/battlefield/mapFile';
import downloadJson, { readJsonFile } from '../utils/downloadJson';

/** The download's file name: the map's own, or the library's with today's date. */
export function mapFileName(name, stamp) {
  return name ? `deathwatch-map-${name.replace(/[^\w-]+/g, '-')}.json` : `deathwatch-maps-${stamp.slice(0, 10)}.json`;
}

/** Read one map file; the toast says what happened either way. */
async function importFile(file, { importMaps, pushToast }) {
  let parsed;
  try {
    parsed = await readJsonFile(file);
  } catch (err) {
    pushToast(err.message, { type: 'error' });
    return;
  }
  const { maps, error } = readMapFile(parsed);
  if (error) {
    pushToast(error, { type: 'error' });
    return;
  }
  importMaps(maps);
  pushToast(`Imported ${maps.length} map${maps.length === 1 ? '' : 's'}.`, { type: 'success' });
}

/** @param {object} args - library, importMaps, pushToast. */
export default function useShellMapFiles({ library, importMaps, pushToast }) {
  const exportMaps = useCallback(
    (name) => {
      const entries = name ? library.filter((e) => e.name === name) : library;
      if (!entries.length) return;
      const stamp = new Date().toISOString();
      downloadJson(toMapFile(entries, stamp), mapFileName(name, stamp));
    },
    [library],
  );
  const importMapEntries = useCallback(
    (maps) => {
      if (maps?.length) importMaps(maps);
    },
    [importMaps],
  );
  const importMapsFromFile = useCallback(
    (file) => importFile(file, { importMaps, pushToast }),
    [importMaps, pushToast],
  );
  return { exportMaps, importMapEntries, importMapsFromFile };
}
